import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Mail, MessageSquare, CheckCircle, Download, ExternalLink } from 'lucide-react';

interface DemoOption {
  value: string;
  viewValue: string;
}

interface AppointmentFormData {
  attendeeEmail: string;
  title: string[];
  description: string;
  location: string;
  startDate: string;
  startTime: string;
}


const BookingDemo: React.FC = () => {
  const [formData, setFormData] = useState<AppointmentFormData>({
    attendeeEmail: '',
    title: [],
    description: 'Appointment with Logicloud team @ https://radio.logicloud.mx/listener',
    location: 'Online',
    startDate: '',
    startTime: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof AppointmentFormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [mailSuccess, setMailSuccess] = useState<boolean | undefined>(undefined);
  const [googleCalendarUrl, setGoogleCalendarUrl] = useState('');
  const [clientDownloadCalendarUrl, setClientDownloadCalendarUrl] = useState('');

  const demoOptions: DemoOption[] = [
    { value: 'WebRTC meetings', viewValue: 'WebRTC meetings' },
    { value: 'Live transcription', viewValue: 'Live transcription' },
    { value: 'Video transcription', viewValue: 'Video transcription' },
    { value: 'Speech to text', viewValue: 'Speech to text' },
    { value: 'Text to speech', viewValue: 'Text to speech' },
    { value: 'Geo-location services', viewValue: 'Geo-location services' },
    { value: 'Other services', viewValue: 'Other services' },
  ];

  const timeSlots: string[] = [
    '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30',
    '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30',
    '19:00', '19:30', '20:00'
  ];

  const [filteredTimeSlots, setFilteredTimeSlots] = useState<string[]>([]);

  const OPENING_HOUR = 9; // Business hours: 09:00
  const CUTOFF_HOUR = 19; // If current time is 19:00 or later, show next weekday

  // Helper function to get next weekday
  const getNextWeekday = (date: Date): Date => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    
    // Skip weekends (Saturday = 6, Sunday = 0)
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    
    return nextDay;
  };

  // Helper function to check if date is a weekday
  const isWeekday = (date: Date): boolean => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // Not Sunday (0) or Saturday (6)
  };

  // Helper function to format date as YYYY-MM-DD (local time, no timezone issues)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize form with smart defaults
  useEffect(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    let initialStartDate: Date;
    let initialStartTime: string;

    // If current time is 19:00 or later, or it's a weekend, show next weekday
    if (currentHour >= CUTOFF_HOUR || !isWeekday(now)) {
      initialStartDate = getNextWeekday(now);
      initialStartDate.setHours(OPENING_HOUR, 0, 0, 0);
      initialStartTime = `${OPENING_HOUR.toString().padStart(2, '0')}:00`;
    } else if (currentHour < OPENING_HOUR) {
      // Before opening hours, use today if weekday
      if (isWeekday(now)) {
        initialStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        initialStartDate.setHours(OPENING_HOUR, 0, 0, 0);
        initialStartTime = `${OPENING_HOUR.toString().padStart(2, '0')}:00`;
      } else {
        // Weekend, use next weekday
        initialStartDate = getNextWeekday(now);
        initialStartDate.setHours(OPENING_HOUR, 0, 0, 0);
        initialStartTime = `${OPENING_HOUR.toString().padStart(2, '0')}:00`;
      }
    } else {
      // Within business hours on a weekday
      if (isWeekday(now)) {
        initialStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const currentMinutes = currentHour * 60 + currentMinute;
        const closestSlot = timeSlots.find(slot => {
          const [h, m] = slot.split(':').map(Number);
          return (h * 60 + m) > currentMinutes + 30; // At least 30 minutes in the future
        });

        if (closestSlot) {
          initialStartTime = closestSlot;
        } else {
          // No more slots today, use next weekday
          initialStartDate = getNextWeekday(now);
          initialStartDate.setHours(OPENING_HOUR, 0, 0, 0);
          initialStartTime = `${OPENING_HOUR.toString().padStart(2, '0')}:00`;
        }
      } else {
        // Weekend, use next weekday
        initialStartDate = getNextWeekday(now);
        initialStartDate.setHours(OPENING_HOUR, 0, 0, 0);
        initialStartTime = `${OPENING_HOUR.toString().padStart(2, '0')}:00`;
      }
    }

    setFormData(prev => ({
      ...prev,
      startDate: formatDateLocal(initialStartDate),
      startTime: initialStartTime
    }));

    updateAvailableTimes(initialStartDate);
  }, []);

  // Helper function to parse date string to local date (avoids timezone issues)
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0); // Create local date at midnight
  };

  const updateAvailableTimes = (selectedDate: Date | null) => {
    if (!selectedDate) {
      setFilteredTimeSlots([]);
      setFormData(prev => ({ ...prev, startTime: '' }));
      return;
    }

    // Check if selected date is a weekday
    if (!isWeekday(selectedDate)) {
      setFilteredTimeSlots([]);
      setFormData(prev => ({ ...prev, startTime: '' }));
      return;
    }

    // Get today's date at midnight (local time)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

    // Normalize selected date to midnight (local time) for comparison
    const selectedDateNormalized = new Date(
      selectedDate.getFullYear(), 
      selectedDate.getMonth(), 
      selectedDate.getDate(), 
      0, 0, 0, 0
    );

    // Compare dates by timestamp
    const isSelectedDateToday = selectedDateNormalized.getTime() === today.getTime();
    const isSelectedDateFuture = selectedDateNormalized.getTime() > today.getTime();

    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // If current time is 19:00 or later, don't show today's slots
    const isTodayPastCutoff = currentHour >= CUTOFF_HOUR;

    if (isSelectedDateToday) {
      // Selected date is today
      if (isTodayPastCutoff) {
        // Past cutoff time, no slots available for today
        setFilteredTimeSlots([]);
        setFormData(prev => ({ ...prev, startTime: '' }));
        return;
      }

      // Today and before cutoff - show available slots from now + 30 minutes
      const availableSlots = timeSlots.filter(slot => {
        const [slotHourStr, slotMinuteStr] = slot.split(':');
        const slotHour = parseInt(slotHourStr, 10);
        const slotMinute = parseInt(slotMinuteStr, 10);

        const slotTimeInMinutes = (slotHour * 60) + slotMinute;
        const currentTimeInMinutes = (currentHour * 60) + currentMinute;

        // Show slots that are at least 30 minutes in the future
        return slotTimeInMinutes > currentTimeInMinutes + 30;
      });

      setFilteredTimeSlots(availableSlots);

      // Clear selected time if it's no longer available
      if (formData.startTime && !availableSlots.includes(formData.startTime)) {
        setFormData(prev => ({ ...prev, startTime: '' }));
      }
    } else if (isSelectedDateFuture) {
      // Selected date is in the future (tomorrow or later) - show ALL time slots
      setFilteredTimeSlots([...timeSlots]);
      
      // Keep the selected time if it's valid, otherwise clear it
      if (formData.startTime && !timeSlots.includes(formData.startTime)) {
        setFormData(prev => ({ ...prev, startTime: '' }));
      }
    } else {
      // Selected date is in the past - should not happen due to min date, but handle it
      setFilteredTimeSlots([]);
      setFormData(prev => ({ ...prev, startTime: '' }));
    }
  };

  // Get minimum selectable date (next weekday if today is past cutoff or weekend)
  const getMinDate = (): string => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // If current time is 19:00 or later, or it's a weekend, min date is next weekday
    if (currentHour >= CUTOFF_HOUR || !isWeekday(now)) {
      const nextWeekday = getNextWeekday(now);
      return formatDateLocal(nextWeekday);
    }
    
    // Otherwise, today is the minimum (if it's a weekday)
    if (isWeekday(now)) {
      return formatDateLocal(now);
    }
    
    // Today is weekend, use next weekday
    const nextWeekday = getNextWeekday(now);
    return formatDateLocal(nextWeekday);
  };

  const handleInputChange = (field: keyof AppointmentFormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));

    if (field === 'startDate' && typeof value === 'string') {
      // Parse date string to local date to avoid timezone issues
      const selectedDate = parseLocalDate(value);
      
      // Validate that selected date is a weekday
      if (!isWeekday(selectedDate)) {
        setErrors(prev => ({ 
          ...prev, 
          startDate: 'Please select a weekday (Monday-Friday)' 
        }));
        setFilteredTimeSlots([]);
        setFormData(prev => ({ ...prev, startTime: '' }));
        return;
      }
      
      // Update available times for the selected date
      updateAvailableTimes(selectedDate);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AppointmentFormData, string>> = {};

    if (!formData.attendeeEmail) {
      newErrors.attendeeEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.attendeeEmail)) {
      newErrors.attendeeEmail = 'Valid email is required';
    }

    if (!formData.title || formData.title.length === 0) {
      newErrors.title = 'Please select at least one option';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Date is required';
    } else {
      // Validate that selected date is a weekday
      const selectedDate = parseLocalDate(formData.startDate);
      if (!isWeekday(selectedDate)) {
        newErrors.startDate = 'Please select a weekday (Monday-Friday)';
      }
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatGoogleCalendarDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const generateICSContent = (startDate: Date, endDate: Date, title: string, description: string, location: string): string => {
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Logicloud//Appointment Scheduler//EN
BEGIN:VEVENT
UID:${Date.now()}@logicloud.mx
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${title}
DESCRIPTION:${description}
LOCATION:${location}
ORGANIZER:CN=logicloud.mx:mailto:info@logicloud.mx
URL:${window.location.origin}
END:VEVENT
END:VCALENDAR`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Execute reCAPTCHA v3
      const captchaToken = await executeRecaptcha('appointment_schedule');

      const startDateObj = new Date(formData.startDate);
      const [hours, minutes] = formData.startTime.split(':').map(Number);
      startDateObj.setHours(hours, minutes, 0, 0);

      const appointmentDurationMinutes = 20;
      const endDateTime = new Date(startDateObj.getTime() + (appointmentDurationMinutes * 60 * 1000));

      const formattedTitle = `Demo for: ${formData.title.join(', ')}`;

      const icsContent = generateICSContent(startDateObj, endDateTime, formattedTitle, formData.description, formData.location);

      // Generate Google Calendar URL
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE` +
        `&text=${encodeURIComponent(formattedTitle)}` +
        `&dates=${formatGoogleCalendarDate(startDateObj)}/${formatGoogleCalendarDate(endDateTime)}` +
        `&details=${encodeURIComponent(formData.description || '')}` +
        `&location=${encodeURIComponent(formData.location || 'Online')}` +
        `&sf=true&output=xml`;

      setGoogleCalendarUrl(googleUrl);

      // Generate ICS download URL
      const icsUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
      setClientDownloadCalendarUrl(icsUrl);

      // Generate email body
      const emailBody = `
        <p>Hi,</p>
        <p>Here are the details for your appointment:</p>
        <ul style="list-style-type: none; padding: 0;">
          <li><strong>Title:</strong> ${formattedTitle}</li>
          <li><strong>Date:</strong> ${startDateObj.toLocaleDateString()}</li>
          <li><strong>Time:</strong> ${startDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</li>
          <li><strong>Location:</strong> ${formData.location} at ${formData.description}</li>
        </ul>
        <p>
          You can add this appointment to your calendar using the links below:<br>
          <a href="${googleUrl}" target="_blank" style="display:inline-block; padding: 8px 15px; background-color: #4285F4; color: white; text-decoration: none; border-radius: 5px; margin-right: 10px;">Add to Google Calendar</a>
          <a href="${icsUrl}" download="appointment.ics" style="display:inline-block; padding: 8px 15px; background-color: #ccc; color: black; text-decoration: none; border-radius: 5px;">Download .ics File</a>
        </p>
        <p>We look forward to meeting you!</p>
        <p>Best regards,<br>
        The Logicloud Team <br>
        <img decoding="async" width="261" height="50" src="https://logicloud.mx/wp-content/uploads/2025/03/LogiCloud-Logo-2.png" alt="LogiCloud-Logo-2" class="wp-image-141 size-small">
        </p>
      `;

      // Get user's IP address (same as contact form)
      const getUserIP = async (): Promise<string> => {
        try {
          const response = await fetch('https://api.ipify.org?format=json');
          const data = await response.json();
          return data.ip;
        } catch (error) {
          console.warn('Could not detect IP address:', error);
          return 'Unknown';
        }
      };

      const userIP = await getUserIP();

      const payload = {
        attendeeEmail: formData.attendeeEmail,
        title: formattedTitle,
        description: formData.description,
        location: formData.location,
        startDate: startDateObj.toISOString(),
        endDate: endDateTime.toISOString(),
        icsContent: icsContent,
        detectedIP: userIP,
        From: 'info@logicloud.mx',
        To: formData.attendeeEmail,
        Subject: `Appointment Confirmation: ${formattedTitle}`,
        Name: formData.attendeeEmail,
        Phone: '',
        Body: emailBody,
        CaptchaToken: captchaToken
      };

      // Send email via API using sendMailToCustomer endpoint
      const response = await fetch('https://api.programmingalternatives.com/api/v1/Comms/sendMailToCustomer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'Authorization': 'Basic Mzo5ODUxRjgwODNERTI1Q0QwNUFDOTBDMzI4RjQ3MTFENDlDRURGNDE5OEI1NkIzQTZDQkY2MEY4RjBGMjUzREVD'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('Booking form response:', result);

      // Handle different response formats (same as contact form)
      let isSuccess = false;
      if (result && typeof result === 'object') {
        if (result.success !== undefined) {
          isSuccess = result.success;
        } else if (result.status !== undefined) {
          isSuccess = result.status === 'success' || result.status === 200;
        } else if (result.message || result.data) {
          isSuccess = true;
        } else {
          isSuccess = true;
        }
      } else if (result === null || result === undefined) {
        isSuccess = false;
      } else {
        isSuccess = !!result;
      }

      setMailSuccess(isSuccess);

      if (isSuccess) {
        // Reset form
        setFormData({
          attendeeEmail: '',
          title: [],
          description: 'Appointment with Logicloud team @ https://radio.logicloud.mx/listener',
          location: 'Online',
          startDate: '',
          startTime: ''
        });
      }

    } catch (error) {
      console.error('Error submitting appointment:', error);
      setMailSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const executeRecaptcha = async (action: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Check if reCAPTCHA is loaded
      if (typeof window !== 'undefined' && window.grecaptcha) {
        try {
          window.grecaptcha.ready(() => {
            window.grecaptcha.execute('6LfgGncrAAAAAL02_3ic0Z-2iHXGch2ARxjzpHIz', { action })
              .then((token: string) => resolve(token))
              .catch((error: any) => reject(error));
          });
        } catch (error) {
          reject(error);
        }
      } else {
        // Wait a bit for reCAPTCHA to load (it's loaded globally in index.html)
        const checkRecaptcha = setInterval(() => {
          if (window.grecaptcha) {
            clearInterval(checkRecaptcha);
            window.grecaptcha.ready(() => {
              window.grecaptcha.execute('6LfgGncrAAAAAL02_3ic0Z-2iHXGch2ARxjzpHIz', { action })
                .then((token: string) => resolve(token))
                .catch((error: any) => reject(error));
            });
          }
        }, 100);

        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkRecaptcha);
          if (!window.grecaptcha) {
            console.warn('reCAPTCHA not loaded after timeout, proceeding without verification');
            resolve('dev-token'); // Use a dummy token as fallback
          }
        }, 5000);
      }
    });
  };

  if (mailSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto p-6"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-8 h-8 text-green-600" />
          </motion.div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Email sent! Add the appointment to your calendar.</h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <a
              href={clientDownloadCalendarUrl}
              download="appointment.ics"
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-5 h-5 mr-2" />
              Add to Outlook/Apple Calendar
            </a>
            <a
              href={googleCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Add to Google Calendar
            </a>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.section
      id="book-demo"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100"
      role="region"
      aria-label="Book Demo section"
    >
      <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-white">
          <h2 className="text-3xl font-bold mb-2">Schedule Appointment</h2>
          <p className="text-purple-100">Book a demo session with our team to explore our AI and cloud solutions.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email Field */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Your Email *
              </label>
              <input
                type="email"
                value={formData.attendeeEmail}
                onChange={(e) => handleInputChange('attendeeEmail', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.attendeeEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="your.email@example.com"
              />
              {errors.attendeeEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.attendeeEmail}</p>
              )}
            </div>

            {/* Project Interest */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Choose the project of your interest *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {demoOptions.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.title.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleInputChange('title', [...formData.title, option.value]);
                        } else {
                          handleInputChange('title', formData.title.filter(t => t !== option.value));
                        }
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{option.viewValue}</span>
                  </label>
                ))}
              </div>
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Date Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Date * (Weekdays only: Monday - Friday)
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                min={getMinDate()}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.startDate ? 'border-red-500' : 'border-gray-300'
                }`}
                onBlur={(e) => {
                  // Additional validation on blur
                  if (e.target.value) {
                    const selectedDate = parseLocalDate(e.target.value);
                    if (!isWeekday(selectedDate)) {
                      setErrors(prev => ({ 
                        ...prev, 
                        startDate: 'Please select a weekday (Monday-Friday)' 
                      }));
                      setFilteredTimeSlots([]);
                      setFormData(prev => ({ ...prev, startTime: '' }));
                    }
                  }
                }}
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
              )}
              {formData.startDate && !isWeekday(parseLocalDate(formData.startDate)) && (
                <p className="text-amber-600 text-sm mt-1">
                  ⚠️ Weekends are not available. Please select a weekday.
                </p>
              )}
            </div>

            {/* Time Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-2" />
                Time *
              </label>
              <select
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                disabled={filteredTimeSlots.length === 0}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.startTime ? 'border-red-500' : 'border-gray-300'
                } ${filteredTimeSlots.length === 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select a time</option>
                {filteredTimeSlots.map((slot) => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
              {errors.startTime && (
                <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>
              )}
            </div>

            {/* Description Field */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Tell us more about your project or specific requirements..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={loading || !formData.attendeeEmail || formData.title.length === 0 || !formData.startDate || !formData.startTime}
              className={`w-full py-4 px-6 rounded-lg font-medium text-white transition-all ${
                loading || !formData.attendeeEmail || formData.title.length === 0 || !formData.startDate || !formData.startTime
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transform hover:scale-105'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Scheduling Appointment...
                </div>
              ) : (
                'Schedule Appointment'
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </motion.section>
  );
};

export default BookingDemo;
